// frontend/src/services/adminService.js - Complete Employee Management API Integration

import { api } from './api.js';

export const adminService = {
  // ===============================================
  // DASHBOARD ENDPOINTS
  // ===============================================

  // Get dashboard statistics
  async getDashboardStats() {
    try {
      console.log('📊 Fetching dashboard stats...');
      const response = await api.get('/admin/dashboard/stats');
      console.log('✅ Dashboard stats received:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Dashboard stats error:', error);

      // Return safe fallback data that matches backend structure
      return {
        success: true,
        stats: {
          totalEmployees: 0,
          todayAttendance: 0,
          pendingLeaves: 0,
          activeFieldWork: 0,
          securityAlerts: 0,
        },
        recentActivity: [],
        timestamp: new Date().toISOString(),
        message: 'Dashboard data temporarily unavailable',
        _fallback: true,
      };
    }
  },

  // Get dashboard activities
  async getDashboardActivities() {
    try {
      console.log('📋 Fetching dashboard activities...');
      const response = await api.get('/admin/dashboard/activities');
      return response.data;
    } catch (error) {
      console.error('❌ Dashboard activities error:', error);
      return {
        success: true,
        activities: [],
        timestamp: new Date().toISOString(),
        message: 'Recent activities temporarily unavailable',
        _fallback: true,
      };
    }
  },

  // ===============================================
  // EMPLOYEE MANAGEMENT ENDPOINTS
  // ===============================================

  // Get users (for managers/admin dropdowns)
  async getUsers(params = {}) {
    try {
      console.log('👥 Fetching users with params:', params);
      const response = await api.get('/admin/users', {
        params: {
          page: params.page || 1,
          limit: params.limit || 100,
          role: params.role || '',
          search: params.search || '',
          ...params,
        },
      });
      console.log('✅ Users fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      throw error;
    }
  },

  // Get all employees with pagination, search, and filters
  async getAllEmployees(params = {}) {
    try {
      console.log('👥 Fetching employees with params:', params);
      const apiParams = {
        page: params.page || 1,
        limit: params.limit || 100, // Get more employees for the frontend
        search: params.search || '', // Use 'search' parameter
        department: params.department || '', // Use 'department' parameter
        role: 'employee', // Only get employees, not admins
        ...params,
      };
      console.log('📤 API params being sent:', apiParams);
      const response = await api.get('/admin/employees', {
        params: apiParams,
      });
      console.log('✅ Employees fetched:', response.data);
      console.log('🔍 Raw items:', response.data?.data?.items);
      
      // Transform backend response to match frontend expectations
      const employees = (response.data?.data?.items || []).map(user => ({
        id: user.id,
        employee_id: user.employee?.code || `EMP${user.id.toString().padStart(3, '0')}`,
        name: user.name,
        email: user.email,
        phone: user.employee?.phone || '',
        address: user.employee?.address || '',
        emergency_contact_name: user.employee?.emergencyContactName || '',
        emergency_contact_phone: user.employee?.emergencyContactPhone || '',
        department: user.employee?.departmentName || 'N/A',
        position: user.employee?.position || 'N/A',
        salary: user.employee?.salary || null,
        hire_date: user.employee?.hireDate || user.createdAt,
        work_schedule: user.employee?.workSchedule || null,
        manager_id: user.employee?.managerId || null,
        manager_name: user.employee?.managerName || '',
        is_active: user.isActive,
        created_at: user.createdAt,
        updated_at: user.updatedAt,
      }));
      
      return {
        success: true,
        employees,
        pagination: response.data?.data?.pagination,
      };
    } catch (error) {
      console.error('❌ Error fetching employees:', error);
      throw error;
    }
  },

  // Get single employee details
  async getEmployeeDetails(employeeId) {
    try {
      console.log(`👤 Fetching employee details for ID: ${employeeId}`);
      const response = await api.get(`/admin/employees/${employeeId}`);
      console.log('✅ Employee details fetched:', response.data);
      
      // Transform backend response to match frontend expectations
      const user = response.data?.data;
      if (user) {
        const employee = {
          id: user.id,
          employee_id: user.employee?.code || `EMP${user.id.toString().padStart(3, '0')}`,
          name: user.name,
          email: user.email,
          phone: user.employee?.phone || '', 
          department: user.employee?.departmentName || 'N/A',
          position: user.employee?.position || 'N/A',
          salary: user.employee?.salary || null,
          hire_date: user.employee?.hireDate || user.createdAt,
          is_active: user.isActive,
          created_at: user.createdAt,
          updated_at: user.updatedAt,
        };
        
        return {
          success: true,
          employee,
        };
      }
      
      return response.data;
    } catch (error) {
      console.error(`❌ Error fetching employee ${employeeId}:`, error);
      throw error;
    }
  },

  // Get manager candidates: every active employee and admin in the system
  async getManagerCandidates() {
    try {
      const response = await api.get('/admin/employees', {
        params: { page: 1, limit: 100 },
      });
      return (response.data?.data?.items || [])
        .filter(user => user.isActive)
        .map(user => ({
          id: user.id,
          name: user.name,
          role: user.role,
          position: user.employee?.position || '',
        }));
    } catch (error) {
      console.error('❌ Error fetching manager candidates:', error);
      throw error;
    }
  },

  // Create new employee
  async createEmployee(employeeData) {
    try {
      console.log('➕ Creating new employee:', employeeData);

      // Validate required fields before sending
      const requiredFields = [
        'name',
        'email',
        'password',
        'department',
        'position',
      ];
      const missingFields = requiredFields.filter(
        field => !employeeData[field]
      );

      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      // Transform frontend data to match backend expectations
      const backendPayload = {
        name: employeeData.name,
        email: employeeData.email,
        password: employeeData.password,
        role: 'employee',
        employeeCode: employeeData.employee_id,
        departmentId: employeeData.department && !isNaN(employeeData.department) ? parseInt(employeeData.department) : null,
        position: employeeData.position,
        hireDate: employeeData.hire_date,
        salary: employeeData.salary ? parseFloat(employeeData.salary) : null,
        phone: employeeData.phone || null,
        workSchedule: employeeData.work_schedule || null,
        managerId: employeeData.manager_id ? parseInt(employeeData.manager_id) : null,
        isActive: true,
      };

      const response = await api.post('/admin/employees', backendPayload);
      console.log('✅ Employee created:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating employee:', error);
      throw error;
    }
  },

  // Update employee
  async updateEmployee(employeeId, employeeData) {
    try {
      console.log(`🔄 Updating employee ${employeeId}:`, employeeData);
      
      // Transform frontend data to match backend expectations
      const backendPayload = {
        name: employeeData.name,
        email: employeeData.email,
        employeeCode: employeeData.employee_id,
        departmentId: employeeData.department && !isNaN(employeeData.department) ? parseInt(employeeData.department) : null,
        position: employeeData.position,
        hireDate: employeeData.hire_date,
        salary: employeeData.salary ? parseFloat(employeeData.salary) : null,
        isActive: employeeData.is_active,
      };

      if (employeeData.phone !== undefined) {
        backendPayload.phone = employeeData.phone || null;
      }

      if (employeeData.manager_id !== undefined) {
        backendPayload.managerId = employeeData.manager_id
          ? parseInt(employeeData.manager_id)
          : null;
      }

      if (employeeData.work_schedule !== undefined) {
        backendPayload.workSchedule = employeeData.work_schedule || null;
      }

      // Only include password if it's provided
      if (employeeData.password) {
        backendPayload.password = employeeData.password;
      }
      
      const response = await api.put(
        `/admin/employees/${employeeId}`,
        backendPayload
      );
      console.log('✅ Employee updated:', response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Error updating employee ${employeeId}:`, error);
      throw error;
    }
  },

  // Delete/Deactivate employee (soft delete)
  async deleteEmployee(employeeId) {
    try {
      console.log(`🗑️ Deactivating employee ${employeeId}`);
      const response = await api.delete(`/admin/employees/${employeeId}`);
      console.log('✅ Employee deactivated:', response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Error deactivating employee ${employeeId}:`, error);
      throw error;
    }
  },

  // Toggle employee status (activate/deactivate)
  async toggleEmployeeStatus(employeeId) {
    try {
      console.log(`🔄 Toggling status for employee ${employeeId}`);
      // First get current status
      const employeeDetails = await this.getEmployeeDetails(employeeId);
      const currentStatus = employeeDetails.employee.is_active;

      // Update with opposite status
      const response = await this.updateEmployee(employeeId, {
        is_active: !currentStatus,
      });

      console.log('✅ Employee status toggled:', response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Error toggling employee ${employeeId} status:`, error);
      throw error;
    }
  },

  // Reset employee password
  async resetEmployeePassword(employeeId) {
    try {
      console.log(`🔑 Resetting password for employee ${employeeId}`);

      // Generate new temporary password
      const tempPassword = this.generateTempPassword();

      const response = await api.post(`/admin/users/${employeeId}/reset-password`, {
        newPassword: tempPassword
      });

      // Return the new password for display
      return {
        ...response.data,
        newPassword: tempPassword,
      };
    } catch (error) {
      console.error(
        `❌ Error resetting password for employee ${employeeId}:`,
        error
      );
      throw error;
    }
  },

  // Generate temporary password
  generateTempPassword() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  },

  // ===============================================
  // LEAVE MANAGEMENT ENDPOINTS
  // ===============================================

  // Get leave requests with filtering
  async getLeaveRequests(params = {}) {
    try {
      console.log('📋 Fetching leave requests with params:', params);
      const response = await api.get('/admin/leaves', {
        params: {
          page: params.page || 1,
          limit: params.limit || 20,
          status: params.status || '',
          department_id: params.department || '',
          from_date: params.fromDate,
          to_date: params.toDate,
          user_id: params.userId,
          type_id: params.typeId,
          sortBy: params.sortBy || 'created_at',
          sortOrder: params.sortOrder || 'DESC',
          ...params,
        },
      });
      console.log('✅ Leave requests fetched:', response.data);
      
      // Transform backend response to match frontend expectations
      if (response.data?.success && response.data?.data) {
        const normalizeItem = (item) => ({
          ...item,
          leave_type: item.leave_type ?? item.type_name ?? null,
          department: item.department ?? item.department_name ?? null,
          employee_id: item.employee_id ?? item.employee_code ?? null,
          days: item.days != null ? item.days : (item.quantity != null ? parseFloat(item.quantity) : null),
        });
        return {
          success: true,
          requests: (response.data.data.items || []).map(normalizeItem),
          pagination: response.data.data.pagination || {},
        };
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching leave requests:', error);
      throw error;
    }
  },

  // Get leave request details
  async getLeaveRequestDetails(leaveRequestId) {
    try {
      console.log(`📄 Fetching leave request details for ID: ${leaveRequestId}`);
      const response = await api.get(`/admin/leaves/${leaveRequestId}`);
      console.log('✅ Leave request details fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Error fetching leave request ${leaveRequestId}:`, error);
      throw error;
    }
  },

  // Approve leave request
  async approveLeaveRequest(leaveRequestId, notes = '') {
    try {
      console.log(`✅ Approving leave request ${leaveRequestId}`);
      const response = await api.patch(`/admin/leaves/${leaveRequestId}/approve`, {
        notes: notes,
      });
      console.log('✅ Leave request approved:', response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Error approving leave request ${leaveRequestId}:`, error);
      throw error;
    }
  },

  // Reject leave request
  async rejectLeaveRequest(leaveRequestId, reason = '') {
    try {
      console.log(`❌ Rejecting leave request ${leaveRequestId}`);
      const response = await api.patch(`/admin/leaves/${leaveRequestId}/reject`, {
        reason: reason,
      });
      console.log('✅ Leave request rejected:', response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Error rejecting leave request ${leaveRequestId}:`, error);
      throw error;
    }
  },

  // Cancel approved leave request
  async cancelLeaveRequest(leaveRequestId, reason = '') {
    try {
      console.log(`🚫 Cancelling approved leave request ${leaveRequestId}`);
      const response = await api.patch(`/admin/leaves/${leaveRequestId}/cancel`, {
        reason: reason,
      });
      console.log('✅ Leave request cancelled:', response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Error cancelling leave request ${leaveRequestId}:`, error);
      throw error;
    }
  },

  // Update leave request (general)
  async updateLeaveRequest(leaveRequestId, data) {
    try {
      console.log(`🔄 Updating leave request ${leaveRequestId}:`, data);
      const response = await api.put(`/admin/leaves/${leaveRequestId}`, data);
      console.log('✅ Leave request updated:', response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Error updating leave request ${leaveRequestId}:`, error);
      throw error;
    }
  },

  // Bulk actions on leave requests
  async bulkActionLeaveRequests(action, requestIds, notes = '') {
    try {
      console.log(`🔄 Bulk ${action} for requests:`, requestIds);
      const response = await api.post('/admin/leaves/bulk-action', {
        action,
        requestIds,
        notes,
      });
      console.log(`✅ Bulk ${action} completed:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Error in bulk ${action}:`, error);
      throw error;
    }
  },

  // Get leave calendar
  async getLeaveCalendar(params = {}) {
    try {
      console.log('📅 Fetching leave calendar with params:', params);
      const response = await api.get('/admin/leaves/calendar', { params });
      console.log('✅ Leave calendar fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching leave calendar:', error);
      throw error;
    }
  },

  // Export leave data
  async exportLeaveData(params = {}) {
    try {
      console.log('📤 Exporting leave data with params:', params);
      const response = await api.get('/admin/leaves/export', {
        params,
        responseType: 'blob',
      });
      console.log('✅ Leave data exported');
      return response;
    } catch (error) {
      console.error('❌ Error exporting leave data:', error);
      throw error;
    }
  },

  // Get leave documents
  async getLeaveDocuments(leaveRequestId) {
    try {
      console.log(`📎 Fetching documents for leave request ${leaveRequestId}`);
      const response = await api.get(`/admin/leaves/${leaveRequestId}/documents`);
      console.log('✅ Leave documents fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Error fetching leave documents:`, error);
      throw error;
    }
  },

  // Download leave document
  async downloadLeaveDocument(documentId, filename) {
    try {
      console.log(`📥 Downloading document ${documentId}`);
      const response = await api.get(`/admin/leave-documents/${documentId}/download`, {
        responseType: 'blob',
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `document-${documentId}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ Document downloaded successfully');
      return { success: true };
    } catch (error) {
      console.error(`❌ Error downloading document:`, error);
      throw error;
    }
  },

  // Get leave document for viewing (with authentication)
  async getLeaveDocumentForViewing(documentId) {
    try {
      console.log(`👁️ Fetching document ${documentId} for viewing`);
      const response = await api.get(`/admin/leave-documents/${documentId}/view`, {
        responseType: 'blob',
      });

      // Create a blob URL for the document
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream'
      });
      const url = window.URL.createObjectURL(blob);

      console.log('✅ Document blob URL created');
      return {
        success: true,
        url,
        contentType: response.headers['content-type']
      };
    } catch (error) {
      console.error(`❌ Error fetching document for viewing:`, error);
      throw error;
    }
  },

  // Download all leave documents as ZIP
  async downloadAllLeaveDocuments(leaveRequestId) {
    try {
      console.log(`📦 Downloading all documents for leave request ${leaveRequestId}`);
      const response = await api.get(`/admin/leaves/${leaveRequestId}/documents/download-all`, {
        responseType: 'blob',
      });

      // Create a download link for the ZIP file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `leave-documents-${leaveRequestId}.zip`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ All documents downloaded successfully');
      return { success: true };
    } catch (error) {
      console.error(`❌ Error downloading all documents:`, error);
      throw error;
    }
  },

  // Delete leave document
  async deleteLeaveDocument(documentId) {
    try {
      console.log(`🗑️ Deleting document ${documentId}`);
      const response = await api.delete(`/admin/leave-documents/${documentId}`);
      console.log('✅ Document deleted successfully');
      return response.data;
    } catch (error) {
      console.error(`❌ Error deleting document:`, error);
      throw error;
    }
  },

  // ===============================================
  // ATTENDANCE REPORTS ENDPOINTS
  // ===============================================

  // Get attendance reports
  async getAttendanceReports(params = {}) {
    try {
      console.log('📊 Fetching attendance reports with params:', params);
      const response = await api.get('/admin/attendance/reports', {
        params: {
          start_date: params.start_date || '',
          end_date: params.end_date || '',
          employee_id: params.employee_id || '',
          department: params.department || '',
          ...params,
        },
      });
      console.log('✅ Attendance reports fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching attendance reports:', error);
      throw error;
    }
  },

  // ===============================================
  // SECURITY ENDPOINTS
  // ===============================================

  // Get security analytics
  async getSecurityAnalytics(timeRange = '7d') {
    try {
      console.log(`🔒 Fetching security analytics for ${timeRange}`);
      const response = await api.get('/admin/security/analytics', {
        params: { timeRange },
      });
      console.log('✅ Security analytics fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching security analytics:', error);
      throw error;
    }
  },

  // ===============================================
  // SESSION MANAGEMENT ENDPOINTS (continuing from above)
  // ===============================================

  // ===============================================
  // SESSION MANAGEMENT ENDPOINTS
  // ===============================================

  // Get all sessions
  async getAllSessions(params = {}) {
    try {
      console.log('🔐 Fetching sessions with params:', params);
      const response = await api.get('/admin/sessions', { params });
      console.log('✅ Sessions fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching sessions:', error);
      throw error;
    }
  },

  // Get session statistics
  async getSessionStats() {
    try {
      console.log('📊 Fetching session statistics');
      const response = await api.get('/admin/sessions/stats');
      console.log('✅ Session stats fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching session stats:', error);
      throw error;
    }
  },

  // Terminate session
  async terminateSession(sessionId, reason = '') {
    try {
      console.log(`🔚 Terminating session ${sessionId}`);
      const response = await api.post(
        `/admin/sessions/${sessionId}/terminate`,
        {
          reason,
        }
      );
      console.log('✅ Session terminated:', response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Error terminating session ${sessionId}:`, error);
      throw error;
    }
  },

  // ===============================================
  // LIVE DATA ENDPOINTS FOR DASHBOARD
  // ===============================================

  // Get live attendance data
  async getLiveAttendance() {
    try {
      console.log('🔴 Fetching live attendance data...');
      const response = await api.get('/admin/attendance/live');
      console.log('✅ Live attendance fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching live attendance:', error);
      // Return fallback data for dashboard
      return {
        success: true,
        data: {
          employeesPresent: 0,
          previousDayPresent: 0,
          inOffice: 0,
          fieldWork: 0,
          lateArrivals: [],
          recentActivities: [],
          activeSessions: [],
        },
        timestamp: new Date().toISOString(),
        message: 'Live data temporarily unavailable',
        _fallback: true,
      };
    }
  },

  // ===============================================
  // ADMIN SESSION MANAGEMENT
  // ===============================================

  // Get current admin's sessions
  async getMysessions() {
    try {
      console.log('👤 Fetching my sessions...');
      const response = await api.get('/admin/my-sessions');
      console.log('✅ My sessions fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching my sessions:', error);
      throw error;
    }
  },

  // Extend current session
  async extendSession() {
    try {
      console.log('⏰ Extending current session...');
      const response = await api.post('/admin/extend-session');
      console.log('✅ Session extended:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error extending session:', error);
      throw error;
    }
  },

  // Terminate other sessions (keep current)
  async terminateOtherSessions(reason = 'admin_terminated_others') {
    try {
      console.log('🔚 Terminating other sessions...');
      const response = await api.post('/admin/terminate-other-sessions', {
        reason,
      });
      console.log('✅ Other sessions terminated:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error terminating other sessions:', error);
      throw error;
    }
  },

  // Get session status (for session timeout warnings)
  async getSessionStatus() {
    try {
      console.log('📊 Checking session status...');
      const response = await api.get('/admin/session-status');
      console.log('✅ Session status fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching session status:', error);
      throw error;
    }
  },

  // Report suspicious activity
  async reportSuspiciousActivity(activityType, description = '', details = {}) {
    try {
      console.log('⚠️ Reporting suspicious activity...');
      const response = await api.post('/admin/report-suspicious-activity', {
        activityType,
        description,
        details,
      });
      console.log('✅ Suspicious activity reported:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error reporting suspicious activity:', error);
      throw error;
    }
  },

  // ===============================================
  // UTILITY METHODS
  // ===============================================

  // Get departments (for form dropdowns)
  async getDepartments() {
    try {
      console.log('📋 Fetching departments from API...');
      const response = await api.get('/admin/departments', {
        params: {
          active: true,
          limit: 100
        }
      });
      console.log('✅ Departments fetched:', response.data);
      
      if (response.data.success && response.data.data?.departments) {
        // Return array of department objects with id and name
        return response.data.data.departments.map(dept => ({
          id: dept.id,
          name: dept.name,
          code: dept.code
        }));
      }
      
      // Fallback: create default departments if API doesn't return data
      console.warn('⚠️ Departments API returned unexpected format, using default departments');
      return [
        { id: 1, name: 'Human Resources', code: 'HR' },
        { id: 2, name: 'Engineering', code: 'ENG' },
        { id: 3, name: 'Sales', code: 'SALES' },
        { id: 4, name: 'Marketing', code: 'MKT' },
        { id: 5, name: 'Finance', code: 'FIN' },
        { id: 6, name: 'Operations', code: 'OPS' },
        { id: 7, name: 'Customer Support', code: 'CS' },
        { id: 8, name: 'Management', code: 'MGMT' },
      ];
    } catch (error) {
      console.error('❌ Error fetching departments:', error);
      // Return default departments if API fails
      return [
        { id: 1, name: 'Human Resources', code: 'HR' },
        { id: 2, name: 'Engineering', code: 'ENG' },
        { id: 3, name: 'Sales', code: 'SALES' },
        { id: 4, name: 'Marketing', code: 'MKT' },
        { id: 5, name: 'Finance', code: 'FIN' },
        { id: 6, name: 'Operations', code: 'OPS' },
        { id: 7, name: 'Customer Support', code: 'CS' },
        { id: 8, name: 'Management', code: 'MGMT' },
      ];
    }
  },

  // Bulk operations
  async bulkUpdateEmployees(employeeIds, updateData) {
    try {
      console.log('🔄 Bulk updating employees:', { employeeIds, updateData });

      // Since backend doesn't have bulk endpoint, update individually
      const results = await Promise.allSettled(
        employeeIds.map(id => this.updateEmployee(id, updateData))
      );

      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      console.log(
        `✅ Bulk update completed: ${successful} successful, ${failed} failed`
      );

      return {
        success: true,
        successful,
        failed,
        total: employeeIds.length,
      };
    } catch (error) {
      console.error('❌ Error in bulk update:', error);
      throw error;
    }
  },

  // ===============================================
  // NOTIFICATIONS
  // ===============================================

  async getNotifications(params = {}) {
    const response = await api.get('/admin/notifications', { params });
    return response.data;
  },

  async markNotificationAsRead(notificationId) {
    const response = await api.put(`/admin/notifications/${notificationId}/read`);
    return response.data;
  },

  async markAllNotificationsAsRead() {
    const response = await api.put('/admin/notifications/mark-all-read');
    return response.data;
  },

  async deleteNotification(notificationId) {
    const response = await api.delete(`/admin/notifications/${notificationId}`);
    return response.data;
  },

  // Export employees data
  async exportEmployees(params = {}) {
    try {
      console.log('📊 Exporting employees...');
      const queryParams = {
        format: params.format || 'csv',
        department: params.department,
        status: params.status,
        ...params
      };

      if (params.format === 'csv' || !params.format) {
        const response = await api.get('/admin/employees/export', {
          params: queryParams,
          responseType: 'blob',
        });

        // Create download link
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `employees-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        console.log('✅ Employees exported successfully');
        return { success: true, message: 'Employees exported successfully' };
      } else {
        const response = await api.get('/admin/employees/export', {
          params: queryParams,
        });
        console.log('✅ Employee export data retrieved');
        return response.data;
      }
    } catch (error) {
      console.error('❌ Error exporting employees:', error);
      throw error;
    }
  },

  // Bulk import employees
  async bulkImportEmployees(fileData) {
    try {
      console.log('📤 Bulk importing employees...');
      
      const formData = new FormData();
      formData.append('file', fileData);
      
      const response = await api.post('/admin/employees/bulk-import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('✅ Employees imported successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error importing employees:', error);
      throw error;
    }
  },

  // ===============================================
  // SHORT LEAVE DEBTS ENDPOINTS
  // ===============================================

  // Get short leave debts
  async getShortLeaveDebts(params = {}) {
    try {
      console.log('📊 Fetching short leave debts with params:', params);
      const response = await api.get('/admin/short-leave-debts', { params });
      console.log('✅ Short leave debts fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching short leave debts:', error);
      throw error;
    }
  },

  // Get short leave debts summary
  async getShortLeaveDebtSummary(params = {}) {
    try {
      console.log('📊 Fetching short leave debts summary with params:', params);
      const response = await api.get('/admin/short-leave-debts/summary', { params });
      console.log('✅ Short leave debts summary fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching short leave debts summary:', error);
      throw error;
    }
  },

  // Permanently wipe all data for an employee (hard delete)
  async wipeEmployee(employeeId) {
    try {
      console.log(`🗑️ Wiping all data for employee ${employeeId}`);
      const response = await api.delete(`/admin/employees/${employeeId}/wipe`);
      console.log('✅ Employee wiped:', response.data);
      return response.data;
    } catch (error) {
      console.error(`❌ Error wiping employee ${employeeId}:`, error);
      throw error;
    }
  },
};

export default adminService;
